import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Checkbox, MessageBanner, TextField } from "@folio/stripes/components";
import { MAX_ISSUE_NUMBER } from "../../utils/issueNumber";
import { useIssueNumberListFormatter } from "../../hooks/useIssueNumberListFormatter";
import { NUMBERING_MESSAGE_PREFIX as MESSAGE_PREFIX } from "../../utils/issueNumberList";
import { ContinuousNumberingPreview } from "./ContinuousNumberingPreview";
import { NumberingChoiceFieldset, } from "./NumberingChoiceFieldset";
import styles from "./PeriodicalCheckSettingsDialog.module.css";
var UNKNOWN_LABEL = "…";
var numbersOf = function (preview, label) { var _a; return (_a = preview === null || preview === void 0 ? void 0 : preview.volumes.find(function (volume) { return volume.label === label; })) === null || _a === void 0 ? void 0 : _a.numbersBefore; };
var backwardNumbers = function (start, count) {
    return Array.from({ length: count }, function (_, index) { return start - count + index; });
};
export var ContinuousNumberingSection = function (_a) {
    var _b, _c, _d, _e;
    var settings = _a.settings, draft = _a.draft;
    var formatMessage = useIntl().formatMessage;
    var formatNumbers = useIssueNumberListFormatter();
    var stored = draft.stored, storedStart = draft.storedStart, continuous = draft.continuous, setContinuous = draft.setContinuous, startInput = draft.startInput, setStartInput = draft.setStartInput, startNumber = draft.startNumber, startValid = draft.startValid, pastVolumes = draft.pastVolumes, setPastVolumes = draft.setPastVolumes, disableMode = draft.disableMode, setDisableMode = draft.setDisableMode, action = draft.action, preview = draft.preview, loading = draft.loading, previewError = draft.previewError, planStale = draft.planStale;
    var enabling = action === "ENABLE";
    var disabling = action === "DISABLE" && stored;
    var anchorLabel = (_c = (_b = settings.continuousNumberingAnchorVolumeLabel) !== null && _b !== void 0 ? _b : preview === null || preview === void 0 ? void 0 : preview.anchorVolumeLabel) !== null && _c !== void 0 ? _c : UNKNOWN_LABEL;
    var currentLabel = (_e = (_d = settings.currentVolumeLabel) !== null && _d !== void 0 ? _d : preview === null || preview === void 0 ? void 0 : preview.currentVolumeLabel) !== null && _e !== void 0 ? _e : UNKNOWN_LABEL;
    var startShown = startNumber !== null && startNumber !== void 0 ? startNumber : UNKNOWN_LABEL;
    var marker = null;
    if (continuous !== stored) {
        marker = formatMessage({
            id: "".concat(MESSAGE_PREFIX, ".").concat(continuous ? "willEnable" : "willDisable"),
        });
    }
    var pastOptions = useMemo(function () {
        var _a, _b, _c;
        if (!preview)
            return [];
        var lastOlder = preview.olderVolumeLabels[preview.olderVolumeLabels.length - 1];
        var lastOlderCount = (_b = (_a = numbersOf(preview, lastOlder)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        var blocked = !preview.renumberBackwardAvailable;
        var example = !blocked && startNumber !== null && lastOlderCount > 0
            ? formatNumbers(backwardNumbers(startNumber, lastOlderCount))
            : null;
        return [
            {
                value: "KEEP_PER_VOLUME",
                recommended: true,
                title: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.keep.title") }),
                description: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.keep.description") }, { volume: currentLabel, number: startShown }),
            },
            {
                value: "RENUMBER_BACKWARD",
                title: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.backward.title") }),
                description: example
                    ? formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.backward.descriptionExample") }, { volume: lastOlder, numbers: example })
                    : formatMessage({
                        id: "".concat(MESSAGE_PREFIX, ".past.backward.description"),
                    }),
                disabledReason: blocked
                    ? formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.backward.unavailable") }, {
                        count: preview.renumberBackwardIssueCount,
                        lowest: (_c = preview.renumberBackwardLowestNumber) !== null && _c !== void 0 ? _c : (startNumber !== null && startNumber !== void 0 ? startNumber : 0) - preview.renumberBackwardIssueCount,
                    })
                    : null,
            },
        ];
    }, [
        preview,
        startNumber,
        startShown,
        currentLabel,
        formatMessage,
        formatNumbers,
    ]);
    var disableOptions = useMemo(function () {
        var _a;
        return [
            {
                value: "RESET",
                recommended: true,
                title: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".disable.reset.title") }),
                description: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".disable.reset.description") }, { volume: currentLabel }),
            },
            {
                value: "KEEP_CURRENT",
                title: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".disable.keep.title") }, { volume: currentLabel }),
                description: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".disable.keep.description") }, {
                    volume: currentLabel,
                    numbers: formatNumbers((_a = numbersOf(preview, currentLabel)) !== null && _a !== void 0 ? _a : []),
                }),
            },
        ];
    }, [preview, currentLabel, formatMessage, formatNumbers]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Checkbox, { checked: continuous, onChange: function () { return setContinuous(!continuous); }, label: React.createElement(React.Fragment, null,
                formatMessage({
                    id: "ui-br-legaldeposits.periodicalCheck.settings.continuousNumbering",
                }),
                marker && React.createElement("span", { className: styles.changedMarker }, marker)) }),
        React.createElement("p", { className: styles.hint }, formatMessage({
            id: "ui-br-legaldeposits.periodicalCheck.settings.continuousNumberingHint",
        })),
        continuous && (React.createElement("div", { className: styles.nestedField },
            React.createElement(TextField, { type: "number", min: 1, max: MAX_ISSUE_NUMBER, value: startInput, onChange: function (e) {
                    return setStartInput(e.target.value);
                }, label: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".startLabel") }, { volume: stored ? anchorLabel : currentLabel }), error: startValid
                    ? undefined
                    : formatMessage({ id: "".concat(MESSAGE_PREFIX, ".startError") }, { max: MAX_ISSUE_NUMBER }), required: true, marginBottom0: true }),
            stored && storedStart !== null && (React.createElement("p", { className: styles.fieldHint }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".startHintCurrent") }, { number: storedStart, volume: anchorLabel }))),
            !stored && settings.suggestedContinuousStartNumber !== null && (React.createElement("p", { className: styles.fieldHint }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".startHintSuggested") }, { number: settings.suggestedContinuousStartNumber }))))),
        enabling && preview && preview.olderVolumeLabels.length > 0 && (React.createElement(NumberingChoiceFieldset, { name: "continuous-numbering-past-volumes", legend: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.legend") }, { volumes: preview.olderVolumeLabels.join(", ") }), warning: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".past.warning") }, { volumes: preview.olderVolumeLabels.join(", ") }), value: pastVolumes, options: pastOptions, onChange: setPastVolumes })),
        disabling && (React.createElement(NumberingChoiceFieldset, { name: "continuous-numbering-disable-mode", legend: formatMessage({ id: "".concat(MESSAGE_PREFIX, ".disable.legend") }, { volume: currentLabel }), value: disableMode, options: disableOptions, onChange: setDisableMode })),
        planStale && (React.createElement(MessageBanner, { type: "warning" }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".stale") }))),
        previewError && (React.createElement(MessageBanner, { type: "error" }, previewError)),
        loading && React.createElement("p", null, formatMessage({ id: "ui-br-legaldeposits.loading" })),
        preview && (React.createElement(ContinuousNumberingPreview, { preview: preview, continuous: continuous }))));
};
